import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Activity } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/EmptyState';
import { BatonEventRow } from '@/components/BatonEventRow';
import { Pagination } from '@/components/Pagination';
import { ApiError } from '@/lib/fetcher';
import { accountEventsKey, fetchAccountEvents } from '@/lib/queries';

/**
 * The per-baton Activity tab. Reads the cross-baton account events route
 * narrowed to this baton (`?batonId=`) — which is **egress-free**, unlike
 * the per-baton `/event-logs` route (that one meters egress; ruling C).
 */
export function ActivityTab({ id }: { id: string }) {
  const [page, setPage] = React.useState({ limit: 20, offset: 0 });
  const q = useQuery({
    queryKey: accountEventsKey({ batonId: id, ...page }),
    queryFn: () => fetchAccountEvents({ batonId: id, ...page }),
  });

  // The events endpoint is cursor-style (hasMore), not total-counted; derive
  // a Pagination-compatible `total` so Prev/Next work without a count query.
  const derivedTotal = q.data
    ? page.offset + q.data.data.length + (q.data.meta.hasMore ? 1 : 0)
    : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Activity</CardTitle>
        <CardDescription>This baton's event log — newest first.</CardDescription>
      </CardHeader>
      <CardContent>
        {q.isPending ? (
          <div className="space-y-2">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : q.error ? (
          <Alert variant="destructive">
            <AlertTitle>
              Couldn't load activity (
              {q.error instanceof ApiError ? q.error.code : 'unknown'})
            </AlertTitle>
            <AlertDescription>
              <Button
                size="sm"
                variant="outline"
                className="mt-2"
                onClick={() => q.refetch()}
              >
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        ) : q.data && q.data.data.length === 0 && page.offset === 0 ? (
          <EmptyState
            icon={Activity}
            title="No activity yet"
            description="Events appear here as the baton is read, appended, extended, shared, or witnessed."
          />
        ) : q.data ? (
          <>
            <ul>
              {q.data.data.map((e) => (
                <BatonEventRow key={e.id} event={e} />
              ))}
            </ul>
            <div className="mt-3">
              <Pagination
                total={derivedTotal}
                limit={page.limit}
                offset={page.offset}
                onChange={setPage}
              />
            </div>
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}
